import type { Noto, NotoCreateArgs } from '@dialpad/noto';
import { createNoto } from '@dialpad/noto';

export enum PowerdialerNotoType {
  AutoDismiss = 'powerdialer.toast.auto',
  SemiManualDismiss = 'powerdialer.toast.semi-manual',
  Error = 'powerdialer.toast.error',
  SyncError = 'powerdialer.toast.sync-error',
  SyncSuccess = 'powerdialer.toast.sync-success',
}

/**
 * Severities linked to Dialtone Toast kind
 */
export enum ToastSeverity {
  Base = 'base',
  Error = 'error',
  Info = 'info',
  Success = 'success',
  Warning = 'warning',
}

export type PowerdialerAutoDismissToast = Noto<
  {
    severity: ToastSeverity;
  },
  PowerdialerNotoType.AutoDismiss
>;
export type PowerdialerSemiManualToast = Noto<
  {
    severity: ToastSeverity;
  },
  PowerdialerNotoType.SemiManualDismiss
>;

export type PowerdialerErrorToast = Noto<
  {
    severity: ToastSeverity;
  },
  PowerdialerNotoType.Error
>;

export type PowerdialerSyncErrorToast = Noto<
  {
    severity: ToastSeverity;
  },
  PowerdialerNotoType.SyncError
>;

export type PowerdialerSyncSuccessToast = Noto<
  {
    severity: ToastSeverity;
  },
  PowerdialerNotoType.SyncSuccess
>;

type DefaultNotoConfig<T extends Noto> = Pick<
  NotoCreateArgs<T['meta'], T['type']>,
  'config' | 'meta' | 'content'
>;
const defaultNotoArgs: Partial<
  Record<PowerdialerNotoType, DefaultNotoConfig<Noto>>
> = {
  [PowerdialerNotoType.AutoDismiss]: {
    config: {
      ttl: 3,
    },
    meta: {
      severity: ToastSeverity.Success,
    },
  } as DefaultNotoConfig<PowerdialerAutoDismissToast>,
  [PowerdialerNotoType.SemiManualDismiss]: {
    config: {
      ttl: 5,
    },
    meta: {
      severity: ToastSeverity.Info,
    },
  } as DefaultNotoConfig<PowerdialerSemiManualToast>,
  [PowerdialerNotoType.Error]: {
    config: {
      ttl: 5,
    },
    meta: {
      severity: ToastSeverity.Error,
    },
    content: {
      title: { key: 'TOAST_ERROR_TITLE' },
    },
  },
  [PowerdialerNotoType.SyncError]: {
    config: {
      // retry is exponential with a max delay of 15s
      ttl: 20,
      mergeRelated: true,
    },
    meta: {
      severity: ToastSeverity.Error,
    },
    content: {
      title: { key: 'TOAST_SYNC_FAILED_TITLE' },
      body: { key: 'TOAST_SYNC_FAILED_BODY' },
    },
  },
  [PowerdialerNotoType.SyncSuccess]: {
    config: {
      ttl: 3,
      mergeRelated: true,
    },
    meta: {
      severity: ToastSeverity.Success,
    },
  },
};

export const createNotoOfTypeFactory =
  <T extends Noto>(type: PowerdialerNotoType) =>
  (
    args: Omit<NotoCreateArgs<T['meta'], T['type']>, 'type'>,
  ): ReturnType<typeof createNoto<T['meta'], T['type']>> =>
    createNoto<T['meta'], T['type']>({
      ...args,
      content: {
        ...(defaultNotoArgs[type]?.content as T['content']),
        ...args.content,
      },
      config: {
        ...defaultNotoArgs[type]?.config,
        ...args.config,
      },
      meta: {
        ...(defaultNotoArgs[type]?.meta as T['meta']),
        ...args.meta,
      },
      type,
    });

export const showAutoDismissToast =
  createNotoOfTypeFactory<PowerdialerAutoDismissToast>(
    PowerdialerNotoType.AutoDismiss,
  );

export const showSemiManualDismissToast =
  createNotoOfTypeFactory<PowerdialerSemiManualToast>(
    PowerdialerNotoType.SemiManualDismiss,
  );

export const showErrorToast =
  createNotoOfTypeFactory<PowerdialerAutoDismissToast>(
    PowerdialerNotoType.Error,
  );

export const showSyncErrorToast =
  createNotoOfTypeFactory<PowerdialerSyncErrorToast>(
    PowerdialerNotoType.SyncError,
  );

export const showSyncSuccessToast =
  createNotoOfTypeFactory<PowerdialerSyncSuccessToast>(
    PowerdialerNotoType.SyncSuccess,
  );
